import React, { FC, ChangeEvent, FormEvent } from 'react'
import { useDispatch } from 'react-redux'
import useTypedSelector from 'interfaces/useTypedSelector'
import { Columns, Column } from 'components/base/grid/grid'
import { StrongLabel } from 'components/base/forms/label'
import { Input, Textarea, Wrapper } from 'components/base/forms/input'
import { Flex } from 'rebass'
import Button from 'components/base/buttons/buttons'
import toastMessage from 'components/utils/toasts/toasts'
import { HR, PSmall } from 'components/base/typography/misc'
import {
	handleFormChange,
	setInitialFormState,
	UserInfoFormData,
	UserInfoInitialFormData,
	initialState,
} from 'reducers/formReducer'
import { Toast, ToastMessage } from 'components/utils/toasts/toasts.styles'
import { Block } from 'components/base/sections/section'
import Checkbox from 'components/base/forms/checkbox'
import { Normal } from 'components/base/typography/misc'

interface Props {
	onSubmit: (data: UserInfoFormData) => void
	formError: string
	submitText?: string
	submitPrimary?: boolean
	initialValues?: UserInfoInitialFormData
	hideKennitala?: boolean
	hideSubscriptionOption?: boolean
}

const UserInfoForm: FC<Props> = ({
	submitText,
	submitPrimary,
	initialValues,
	hideKennitala,
	hideSubscriptionOption,
	onSubmit,
	formError,
}) => {
	const dispatch = useDispatch()
	const {
		companySocialId,
		name,
		jobTitle,
		tel,
		email,
		password,
		confirmPassword,
		comments,
		postlistSelected,
	} = useTypedSelector(({ form }) => form)

	const handleFormInput = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>, id: string) => {
		dispatch(handleFormChange({ value: e.target.value, id }))
	}

	React.useEffect(() => {
		if (initialValues) dispatch(setInitialFormState(initialValues))
	}, [])

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault()

		if (password !== confirmPassword) {
			toastMessage({
				type: 'warning',
				message: 'Lykilorðin passa ekki saman',
			})

			return
		}

		onSubmit({
			companySocialId,
			jobTitle,
			name,
			tel: `+354${tel}`,
			email,
			password,
			comments,
			postlistSelected,
		})
		dispatch(handleFormChange(initialState))
	}

	const handlePostList = (value: boolean) => {
		dispatch(handleFormChange({ value, id: 'postlistSelected' }))
	}

	return (
		<form onSubmit={handleSubmit}>
			<Columns>
				<Column tablet={1 / 2}>
					<StrongLabel mb="1.5rem" htmlFor="name">
						Nafn
					</StrongLabel>
					<Input
						type="text"
						id="name"
						name="name"
						aria-label="Nafn"
						placeholder="Nafn"
						value={name}
						required
						onChange={e => handleFormInput(e, 'name')}
					/>
				</Column>
				<Column tablet={1 / 2}>
					<StrongLabel mb="1.5rem" htmlFor="tel">
						Símanúmer
					</StrongLabel>
					<Wrapper>
						<Input
							type="tel"
							id="tel"
							name="tel"
							aria-label="Símanúmer"
							placeholder="Símanúmer"
							value={tel}
							required
							onChange={e => handleFormInput(e, 'tel')}
							hasPrefix={true}
						/>
					</Wrapper>
					<PSmall mt="0.5rem">Án bila og bandstrika</PSmall>
				</Column>
				<Column tablet={1}>
					<StrongLabel mb="1.5rem" htmlFor="email">
						Netfang
					</StrongLabel>
					<Input
						type="email"
						id="email"
						name="email"
						aria-label="Netfang"
						placeholder="Netfang"
						value={email}
						required
						onChange={e => handleFormInput(e, 'email')}
					/>
				</Column>
				<Column tablet={1}>
					{!hideSubscriptionOption && (
						<Block mt="1rem">
							<Checkbox checked={postlistSelected} onClick={() => handlePostList(!postlistSelected)}>
								<Normal>Skrá mig á póstlista hjá Matarstræti</Normal>
							</Checkbox>
						</Block>
					)}
				</Column>
				<Column tablet={1 / 2}>
					<StrongLabel mt="1.5rem" mb="1.5rem" htmlFor="password">
						Lykilorð
					</StrongLabel>
					<Input
						type="password"
						id="password"
						name="password"
						autoComplete="new-password"
						aria-label="Lykilorð"
						placeholder="Lykilorð"
						minLength={6}
						value={password}
						required
						pattern="^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s).*$"
						onChange={e => handleFormInput(e, 'password')}
					/>
					<PSmall mt="0.5rem">Minnst átta stafir, stór stafur, lítill stafur, tölustafur.</PSmall>
				</Column>
				<Column tablet={1 / 2}>
					<StrongLabel mt="1.5rem" mb="1.5rem" htmlFor="confirm-password">
						Staðfesta Lykilorð
					</StrongLabel>
					<Input
						type="password"
						id="confirm-password"
						name="confirm-password"
						autoComplete="new-password"
						aria-label="Staðfesta Lykilorð"
						placeholder="Staðfesta Lykilorð"
						minLength={8}
						value={confirmPassword}
						required
						pattern="^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s).*$"
						onChange={e => handleFormInput(e, 'confirmPassword')}
					/>
				</Column>
			</Columns>
			<HR mt="3rem" mb="3rem" />
			<Columns>
				{!hideKennitala && (
					<Column tablet={1}>
						<StrongLabel mb="1.5rem" htmlFor="company-social-id">
							Kennitala fyrirtækis
						</StrongLabel>
						<Input
							type="text"
							id="company-social-id"
							name="company-social-id"
							aria-label="Kennitala fyrirtækis"
							value={companySocialId}
							required
							placeholder="Kennitala fyrirtækis"
							minLength={5}
							onChange={e => handleFormInput(e, 'companySocialId')}
						/>
					</Column>
				)}
				<Column tablet={1}>
					<StrongLabel mb="1.5rem" htmlFor="job-title">
						Starfstitill
					</StrongLabel>
					<Input
						type="text"
						id="job-title"
						name="job-title"
						aria-label="Starfstitill"
						value={jobTitle}
						placeholder="Starfstitill"
						onChange={e => handleFormInput(e, 'jobTitle')}
					/>
				</Column>
				<Column tablet={1}>
					<StrongLabel mb="1.5rem" mt="1.5rem" htmlFor="comments">
						Athugasemdir
					</StrongLabel>
					<Textarea
						id="comments"
						name="comments"
						aria-label="Athugasemdir"
						placeholder="Athugasemdir"
						value={comments}
						onChange={e => handleFormInput(e, 'comments')}
					/>
				</Column>
			</Columns>
			<Columns>
				<Column tablet={1 / 2}>
					{formError.length > 0 && (
						<Toast type="warning">
							<ToastMessage>{formError}</ToastMessage>
						</Toast>
					)}
				</Column>
				<Column tablet={1 / 2}>
					<Flex width={1} justifyContent="flex-end" mt="1.5rem">
						<Button type="submit" secondary={!submitPrimary} primary={submitPrimary}>
							{submitText ? submitText : 'Nýskrá'}
						</Button>
					</Flex>
				</Column>
			</Columns>
		</form>
	)
}

export default UserInfoForm
